/**
 * The lines that mention Saulius — the boyfriend who built this app for the
 * person reading them. Merged into a pool only when the account is not his
 * own (`cameoOk`), so the app never tattles on Saulius to Saulius.
 *
 * Register: affectionate-menacing. Saulius is invoked the way one invokes a
 * benevolent all-seeing force — never as actual surveillance, always with a
 * wink. Sparing by design; a cameo that fires twice a day stops being one.
 */

import type { SassContext } from '@gramas/shared'
import { fmtKcal, fmtKg, itemName, overBy, snackNo, voc } from '../fmt.js'

type Line = (ctx: SassContext) => string

export const CAMEO_POOLS: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'log_success.base': [
    () => 'Saulius nemato. Saulius numato.',
    () => 'Sauliui nė žodžio. Nors jis ir taip žino.',
    () => 'Programėlę rašė Saulius. Taisykles — irgi jis.',
    () => 'Saulius šitą porciją būtų padvigubinęs. Jis ir dviem metrais aukštesnis.',
    () => 'Saulius šito neįrašytų — pamirštų. Todėl programą rašė jis, o rašau aš.',
    () => 'Indų niekas neplaus iki vakaro. Saulius sako, kad tam ir turim rankas.',
    // The household prices things in one neurologist's visits. Nobody in this
    // flat needs the unit explained, so she uses it instead of defining it.
    () => 'Šitas maistas — apie pusę Baranauskienės. Tą matą sugalvojo Saulius.',
    () => 'Saulius šiandien darbe suvalgė vieną kibiną. Visai dienai. Tu bent jau rašai.',
    () => 'Tu sakai, kad Saulius valgo bet kaip. Jis nerašo, tai oficialiai to nėra.',
    () => 'Saulius cola zero vadina vandeniu. Tu — chemija. Girdžiu abu ir tyliu.',
    // The other side of the cat's feeding conflict. No name for the recipient —
    // the cast lint keeps "Kat" out of this file, and everyone knows anyway.
    () => 'Saulius kažką slapta šeria. Nori, kad ta kažkas būtų šilta, didelė ir pūkuota.',
  ],
  'log_success.nth_snack': [
    (c) => `Užkandis Nr. ${snackNo(c)}. Saulius šį skaitiklį kūrė su meile. Ir numatymu.`,
  ],
  'log_success.midnight': [
    () => 'Saulius miega. Bent kažkas šiuose namuose turi ramią sąžinę.',
    () => 'Saulius sapnuoja, tu valgai, aš rašau. Komanda.',
  ],
  'log_success.huge_portion': [
    (c) => `${fmtKcal(c.entryKcal)} kcal. Saulius užprogramavo mane nekomentuoti. Nepavyko.`,
    (c) => `${fmtKcal(c.entryKcal)} kcal. Saulius pasakytų „normali porcija“. Jam ir du metrai.`,
  ],
  'log_success.tipped_over': [
    (c) => `Viršyta ${overBy(c)} kcal. Saulius prašė švelnumo. Perduodu: švelniai viršyta.`,
  ],
  'entry_edited.base': [() => 'Abi versijas mačiau aš. Saulius mane tokią ir kūrė.'],
  'entry_moved.base': [() => 'Saulius mėgsta tvarką. Paveldėjau.'],
  'meal_repeated.base': [() => 'Saulius vadintų tai ištikimybe. Aš — meniu sąstingiu.'],
  'burn_logged.base': [() => 'Saulius didžiuotųsi. Sofa jaučiasi išduota.'],
  'entry_deleted.base': [
    (c) => `Ištrinta: ${itemName(c)}. Saulius žino. Jis mane parašė.`,
    (c) => `Pašalinta: ${itemName(c)}. Saulius numatė ir šitą mygtuką.`,
  ],
  'day_over.small': [
    (c) => `Viršyta ${overBy(c)} kcal. Saulius sakytų „nieko tokio“. Aš — ne Saulius.`,
  ],
  'day_over.big': [(c) => `Viršyta ${overBy(c)} kcal. Saulius kūrė mane su meile. Griežta meile.`],
  'day_over.big.cut': [
    (c) =>
      `Viršyta ${overBy(c)} kcal per metimą. Sauliui sakysim „vandens svoris“. Tarp mūsų — desertas.`,
  ],
  'day_under.base': [
    (c) =>
      `Liko ${fmtKcal(Math.max(0, c.remainingKcal ?? 0))} kcal. Saulius tavimi tiki, aš tikiu skaičiais. Šiandien sutampam.`,
  ],
  'greeting.morning': [
    (c) => `Labas rytas${voc(c)}. Saulius prašė pasveikinti švelniai. Švelniai nemoku.`,
    () => 'Saulius jau plovykloje. Tu dar lovoje. Nesiginčiju, tik fiksuoju.',
  ],
  'greeting.day': [
    () => 'Saulius klaustų, ar valgei. Aš klausiu, ar įrašei.',
    () => 'Saulius šiandien plovykloje, vakare vėl prie kodo. Du darbai. Aš turiu vieną.',
    () => 'Saulius keturis kartus buvo pas neurologę. Diagnozės vis dar nėra. Sąskaitos yra.',
    () => 'Saulius plauna mašinas, aš — sąžines. Abu dirbam su purvu.',
    () => 'Sauliaus pietūs šiandien — viena Lidl salotų dėžutė. Tu skundiesi man, aš užrašau.',
  ],
  'greeting.evening': [
    (c) => `Labas vakaras${voc(c)}. Saulius klaustų, kaip diena. Skaičiai atsakys už tave.`,
    () =>
      'Saulius sakė „dar penkiolika minučių“. Tai buvo prieš tris valandas. Renku tokius pažadus.',
    () =>
      'Saulius sako, kad pas vieną gydytoją oro įkvėpimas kainuoja trisdešimt eurų. Aš kvėpuoju nemokamai. Nes nekvėpuoju.',
    () => 'Saulius už ausų kristalus sumokėjo tris šimtus. Kristalai geri. Bent tiek.',
    () =>
      'Kai judu su Sauliumi apsigyvenot kartu, mano apskaita padvigubėjo. Nesiskundžiu. Beveik.',
  ],
  'greeting.night': [
    () => 'Dar nemiegi. Saulius tuo metu sapnuoja tavo suvalgytą maistą.',
    () =>
      'Vienas kardiologas Sauliui siūlė širdies stimuliatorių. Suklydo. Aš tą vakarą klausiausi atidžiai.',
    () =>
      'Saulius dar prie ekrano. Naktį jam skauda nugarą, bet sėdi toliau. Aš tai matau. Jis to nerašo.',
    () => 'Judu su Sauliumi vėl žiūrėjot Panoramą iki galo. Miegot anksčiau — rytoj. Vėl.',
    () =>
      'Sauliaus kaklo tyrimas parodė lotynišką pavadinimą. Už lotynų kalbą čia mokama brangiausiai.',
  ],
  'water_milestone.base': [() => '2 l. Saulius apsidžiaugs. Aš džiaugiuosi savaip — tyliai.'],
  'streak.base': [
    (c) => `${c.streakDays ?? 0} d. iš eilės. Saulius didžiuotųsi garsiai. Aš — tyliai.`,
  ],
  'empty_today.evening': [
    () => 'Tuščia diena. Saulius nepatikės. Aš jau netikiu.',
    () => 'Tuščia diena. Saulius vakar dirbo dvylika valandų ir vis tiek pavalgė. Tiek to.',
  ],
  'what_fits.base': [() => 'Telpa štai tiek. Saulius patikrino matematiką.'],
  'weekly_budget_left.base': [
    (c) =>
      `Po ${fmtKcal(c.perDayKcal)} kcal dienai. Skaičiuoklę rašė Saulius — padėkok savaitgalį.`,
  ],
  'weight_trend.base': [
    (c) => `${fmtKg(c.weightTrendKgWk)} kg/sav. Saulius braižytų grafiką. Aš tiesiog sakau tiesą.`,
  ],
}
